import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem,
  Paper, Select, Switch, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
  CircularProgress, FormControlLabel, OutlinedInput,
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, LocalShipping as ShippingIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import i18n from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const EMPTY_ZONE = { name: '', description: '', states: [] };
const EMPTY_RATE = { warehouseId: '', shippingZoneId: '', rate: '', estimatedDaysMin: '', estimatedDaysMax: '', isActive: true };

// The zone's name in the delete confirmation. A component rather than an interpolated value, so a
// name with "<" in it can't be read as markup by <Trans>.
const QuotedName = ({ name }) => <strong>"{name}"</strong>;

// For the data-loading callbacks, so a language switch doesn't refetch everything.
const tShipping = (key, options) => i18n.t(key, { ns: 'shipping', ...options });

const ShippingManagementPage = () => {
  const { t } = useTranslation('shipping');
  const { getAccessToken } = useAuth();
  const [tab, setTab] = useState(0);

  // --- Zones state ---
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zoneDialog, setZoneDialog] = useState(false);
  const [zoneForm, setZoneForm] = useState({ ...EMPTY_ZONE });
  const [isZoneEdit, setIsZoneEdit] = useState(false);
  const [zoneEditId, setZoneEditId] = useState(null);
  const [zoneDeleteConfirm, setZoneDeleteConfirm] = useState({ open: false, item: null });

  // --- Rates state ---
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [rateDialog, setRateDialog] = useState(false);
  const [rateForm, setRateForm] = useState({ ...EMPTY_RATE });
  const [isRateEdit, setIsRateEdit] = useState(false);
  const [rateEditId, setRateEditId] = useState(null);
  const [rateDeleteConfirm, setRateDeleteConfirm] = useState({ open: false, item: null });

  // Warehouses for dropdown in rates
  const [warehouses, setWarehouses] = useState([]);

  const authHeaders = useCallback(async () => {
    const token = await getAccessToken();
    if (!token) throw new Error(tShipping('errors.authRequired'));
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getAccessToken]);

  // ───────────── Fetch helpers ─────────────

  const fetchZones = useCallback(async () => {
    setZonesLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/shipping-zones`, { headers });
      if (!res.ok) throw new Error(tShipping('errors.fetchZones'));
      const data = await res.json();
      setZones(Array.isArray(data) ? data : data.data || []);
    } catch (err) { toast.error(err.message); }
    finally { setZonesLoading(false); }
  }, [authHeaders]);

  const fetchRates = useCallback(async () => {
    setRatesLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/shipping-rates`, { headers });
      if (!res.ok) throw new Error(tShipping('errors.fetchRates'));
      const data = await res.json();
      setRates(Array.isArray(data) ? data : data.data || []);
    } catch (err) { toast.error(err.message); }
    finally { setRatesLoading(false); }
  }, [authHeaders]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/warehouses`, { headers });
      if (!res.ok) {
        toast.warn(tShipping('errors.warehousesUnavailable'));
        return;
      }
      const data = await res.json();
      setWarehouses(Array.isArray(data) ? data : data.data || []);
    } catch (err) { toast.error(tShipping('errors.loadWarehouses', { detail: err.message })); }
  }, [authHeaders]);

  useEffect(() => {
    fetchZones();
    fetchRates();
    fetchWarehouses();
  }, [fetchZones, fetchRates, fetchWarehouses]);

  // ───────────── Zones CRUD ─────────────

  const openZoneCreate = () => { setIsZoneEdit(false); setZoneEditId(null); setZoneForm({ ...EMPTY_ZONE }); setZoneDialog(true); };

  const openZoneEdit = (z) => {
    setIsZoneEdit(true);
    setZoneEditId(z.id);
    setZoneForm({ name: z.name || '', description: z.description || '', states: z.states || [] });
    setZoneDialog(true);
  };

  const saveZone = async () => {
    if (!zoneForm.name) { toast.error(t('toast.zoneNameRequired')); return; }
    try {
      const headers = await authHeaders();
      const url = isZoneEdit
        ? `${config.API_BASE_URL}/api/v1/content/admin/shipping-zones/${zoneEditId}`
        : `${config.API_BASE_URL}/api/v1/content/admin/shipping-zones`;
      const res = await fetch(url, {
        method: isZoneEdit ? 'PUT' : 'POST', headers,
        body: JSON.stringify(zoneForm),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(translateServerMessage(e.message, t('errors.saveFailed'))); }
      toast.success(isZoneEdit ? t('toast.zoneUpdated') : t('toast.zoneCreated'));
      setZoneDialog(false);
      fetchZones();
    } catch (err) { toast.error(err.message); }
  };

  const deleteZone = async () => {
    if (!zoneDeleteConfirm.item) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/shipping-zones/${zoneDeleteConfirm.item.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(t('errors.deleteFailed'));
      toast.success(t('toast.zoneDeleted'));
      setZoneDeleteConfirm({ open: false, item: null });
      fetchZones();
    } catch (err) { toast.error(err.message); setZoneDeleteConfirm({ open: false, item: null }); }
  };

  // ───────────── Rates CRUD ─────────────

  const openRateCreate = () => { setIsRateEdit(false); setRateEditId(null); setRateForm({ ...EMPTY_RATE }); setRateDialog(true); };

  const openRateEdit = (r) => {
    setIsRateEdit(true);
    setRateEditId(r.id);
    setRateForm({
      warehouseId: r.warehouseId || '',
      shippingZoneId: r.shippingZoneId || '',
      rate: r.rate ?? '',
      estimatedDaysMin: r.estimatedDaysMin ?? '',
      estimatedDaysMax: r.estimatedDaysMax ?? '',
      isActive: r.isActive ?? r.active ?? true,
    });
    setRateDialog(true);
  };

  const saveRate = async () => {
    if (!rateForm.warehouseId || !rateForm.shippingZoneId || rateForm.rate === '') {
      toast.error(t('toast.rateRequired'));
      return;
    }
    try {
      const headers = await authHeaders();
      const body = {
        ...rateForm,
        rate: parseFloat(rateForm.rate),
        estimatedDaysMin: rateForm.estimatedDaysMin !== '' ? parseInt(rateForm.estimatedDaysMin, 10) : null,
        estimatedDaysMax: rateForm.estimatedDaysMax !== '' ? parseInt(rateForm.estimatedDaysMax, 10) : null,
      };
      const url = isRateEdit
        ? `${config.API_BASE_URL}/api/v1/content/admin/shipping-rates/${rateEditId}`
        : `${config.API_BASE_URL}/api/v1/content/admin/shipping-rates`;
      const res = await fetch(url, {
        method: isRateEdit ? 'PUT' : 'POST', headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(translateServerMessage(e.message, t('errors.saveFailed'))); }
      toast.success(isRateEdit ? t('toast.rateUpdated') : t('toast.rateCreated'));
      setRateDialog(false);
      fetchRates();
    } catch (err) { toast.error(err.message); }
  };

  const deleteRate = async () => {
    if (!rateDeleteConfirm.item) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${config.API_BASE_URL}/api/v1/content/admin/shipping-rates/${rateDeleteConfirm.item.id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(t('errors.deleteFailed'));
      toast.success(t('toast.rateDeleted'));
      setRateDeleteConfirm({ open: false, item: null });
      fetchRates();
    } catch (err) { toast.error(err.message); setRateDeleteConfirm({ open: false, item: null }); }
  };

  // ───────────── Helpers ─────────────

  const warehouseName = (id) => warehouses.find((w) => w.id === id)?.name || id;
  const zoneName = (id) => zones.find((z) => z.id === id)?.name || id;

  // ───────────── Render ─────────────

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>{t('title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('subtitle')}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Tooltip title={t('refresh')}>
            <IconButton onClick={() => { fetchZones(); fetchRates(); }} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={tab === 0 ? openZoneCreate : openRateCreate}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
            {tab === 0 ? t('addZone') : t('addRate')}
          </Button>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={t('tabs.zones')} sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label={t('tabs.rates')} sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {/* ─── Zones Tab ─── */}
        {tab === 0 && (
          <TableContainer>
            {zonesLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t('zones.name')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('zones.description')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('zones.states')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {zones.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                        <ShippingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">{t('zones.empty')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    zones.map((z) => (
                      <TableRow key={z.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{z.name}</TableCell>
                        <TableCell>{z.description || '—'}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} flexWrap="wrap">
                            {(z.states || []).map((s) => (
                              <Chip key={s} label={s} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('edit')}><IconButton size="small" onClick={() => openZoneEdit(z)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title={t('delete')}><IconButton size="small" color="error" onClick={() => setZoneDeleteConfirm({ open: true, item: z })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}

        {/* ─── Rates Tab ─── */}
        {tab === 1 && (
          <TableContainer>
            {ratesLoading ? (
              <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t('rates.warehouse')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('rates.zone')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('rates.rate')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('rates.estDays')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('rates.active')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <ShippingIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary">{t('rates.empty')}</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rates.map((r) => (
                      <TableRow key={r.id} hover>
                        <TableCell>{r.warehouseName || warehouseName(r.warehouseId)}</TableCell>
                        <TableCell>{r.shippingZoneName || zoneName(r.shippingZoneId)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>${parseFloat(r.rate || 0).toFixed(2)}</TableCell>
                        <TableCell>
                          {r.estimatedDaysMin != null && r.estimatedDaysMax != null
                            ? `${r.estimatedDaysMin}–${r.estimatedDaysMax}`
                            : r.estimatedDaysMin ?? r.estimatedDaysMax ?? '—'}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const active = r.isActive ?? r.active ?? false;
                            return (
                              <Chip label={active ? t('status.active') : t('status.inactive')} size="small"
                                color={active ? 'success' : 'default'} variant="outlined" />
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('edit')}><IconButton size="small" onClick={() => openRateEdit(r)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title={t('delete')}><IconButton size="small" color="error" onClick={() => setRateDeleteConfirm({ open: true, item: r })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </TableContainer>
        )}
      </Paper>

      {/* ─── Zone Dialog ─── */}
      <Dialog open={zoneDialog} onClose={() => setZoneDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{isZoneEdit ? t('zoneDialog.editTitle') : t('zoneDialog.addTitle')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('zoneDialog.name')} value={zoneForm.name}
                  onChange={(e) => setZoneForm((p) => ({ ...p, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('zoneDialog.description')} multiline rows={2} value={zoneForm.description}
                  onChange={(e) => setZoneForm((p) => ({ ...p, description: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('zoneDialog.states')}</InputLabel>
                  <Select
                    multiple value={zoneForm.states}
                    onChange={(e) => setZoneForm((p) => ({ ...p, states: e.target.value }))}
                    input={<OutlinedInput label={t('zoneDialog.states')} />}
                    renderValue={(sel) => (
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {sel.map((v) => <Chip key={v} label={v} size="small" />)}
                      </Box>
                    )}
                  >
                    {US_STATES.map((s) => (
                      <MenuItem key={s.code} value={s.code}>{s.name} ({s.code})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setZoneDialog(false)} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button onClick={saveZone} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {isZoneEdit ? t('update') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Rate Dialog ─── */}
      <Dialog open={rateDialog} onClose={() => setRateDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{isRateEdit ? t('rateDialog.editTitle') : t('rateDialog.addTitle')}</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('rateDialog.warehouse')}</InputLabel>
                  <Select value={rateForm.warehouseId} label={t('rateDialog.warehouse')}
                    onChange={(e) => setRateForm((p) => ({ ...p, warehouseId: e.target.value }))}>
                    {warehouses.map((w) => <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('rateDialog.zone')}</InputLabel>
                  <Select value={rateForm.shippingZoneId} label={t('rateDialog.zone')}
                    onChange={(e) => setRateForm((p) => ({ ...p, shippingZoneId: e.target.value }))}>
                    {zones.map((z) => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label={t('rateDialog.rate')} type="number" value={rateForm.rate}
                  onChange={(e) => setRateForm((p) => ({ ...p, rate: e.target.value }))}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label={t('rateDialog.minDays')} type="number" value={rateForm.estimatedDaysMin}
                  onChange={(e) => setRateForm((p) => ({ ...p, estimatedDaysMin: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label={t('rateDialog.maxDays')} type="number" value={rateForm.estimatedDaysMax}
                  onChange={(e) => setRateForm((p) => ({ ...p, estimatedDaysMax: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={rateForm.isActive} onChange={(e) => setRateForm((p) => ({ ...p, isActive: e.target.checked }))} color="success" />}
                  label={rateForm.isActive ? t('status.active') : t('status.inactive')}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRateDialog(false)} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button onClick={saveRate} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {isRateEdit ? t('update') : t('create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Confirmations ─── */}
      <Dialog open={zoneDeleteConfirm.open} onClose={() => setZoneDeleteConfirm({ open: false, item: null })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" /> {t('deleteZone.title')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            <Trans t={t} i18nKey="deleteZone.body"
              components={{ name: <QuotedName name={zoneDeleteConfirm.item?.name} /> }} />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setZoneDeleteConfirm({ open: false, item: null })} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button onClick={deleteZone} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 600 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rateDeleteConfirm.open} onClose={() => setRateDeleteConfirm({ open: false, item: null })}
        PaperProps={{ sx: { borderRadius: 3, maxWidth: 440 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="error" /> {t('deleteRate.title')}
        </DialogTitle>
        <DialogContent>
          <Typography>{t('deleteRate.body')}</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setRateDeleteConfirm({ open: false, item: null })} sx={{ textTransform: 'none' }}>{t('cancel')}</Button>
          <Button onClick={deleteRate} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 600 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShippingManagementPage;
